/**
 * Converts a designer AppTheme to a MUI Theme object.
 *
 * Used by FormCanvas WYSIWYG to render the form in the same
 * theme that App mode will use at runtime.
 */
import { createTheme, type Theme } from '@mui/material/styles'
import type { AppTheme } from '../types/theme.types'
import { formChromeFromAppTheme } from '../components/editors/FormEditor/formChromeTheme'

export function appThemeToMui(appTheme: AppTheme): Theme {
  const isDark = appTheme.mode === 'dark'
  const p = appTheme.palette
  const s = appTheme.semantic
  const t = appTheme.typography

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: { main: p.primary[500], light: p.primary[300], dark: p.primary[700] },
      secondary: { main: p.secondary[500], light: p.secondary[300], dark: p.secondary[700] },
      success: { main: p.success[500], light: p.success[300], dark: p.success[700] },
      warning: { main: p.warning[500] },
      error: { main: p.error[500] },
      info: { main: p.info[500] },
      background: {
        default: s.appBackground || (isDark ? '#121212' : '#fafafa'),
        paper: s.surfaceBackground || (isDark ? '#1e1e1e' : '#ffffff'),
      },
      text: {
        primary: s.textPrimary || (isDark ? '#ffffff' : '#212121'),
        secondary: s.textSecondary || (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'),
      },
      divider: s.borderDefault || (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'),
      formChrome: formChromeFromAppTheme(appTheme),
    },
    typography: {
      fontFamily: t.fontFamily || '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      // sizeMd is in rem; MUI's fontSize is in px and drives every pxToRem.
      fontSize: t.sizeMd ? t.sizeMd * 16 : 14,
    },
    shape: {
      borderRadius: appTheme.shape?.borderRadiusMd ?? 8,
    },
    spacing: appTheme.spacing?.baseUnit ?? 8,
  })
}
